import {
  type Role,
  TENANT_TYPES,
  type TenantType,
  assignableRoles,
  roleFitsTenantType,
} from '@ambernord/shared';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Field,
  Input,
  Modal,
  Select,
  SkeletonRows,
  StatusBadge,
  THead,
  TableShell,
  Td,
  Th,
  Tr,
  useToast,
} from '@ambernord/ui';
import { ScrollText, Users } from 'lucide-react';
import { type FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.js';
import { AppLayout, DataState } from '../components/AppLayout.js';
import {
  type AdminTenant,
  adminDeleteInvite,
  adminInviteUser,
  adminListTenants,
  adminResendInvite,
  adminResetMfa,
  api,
} from '../lib/api.js';
import { formatDateTime } from '../lib/format.js';

type Tab = 'users' | 'audit' | 'health';

interface AdminUser {
  id: string;
  tenantId: string;
  email: string;
  role: Role;
  status: string;
  mfaEnabled: boolean;
}

const isPlatformAdminRole = (r: Role) => r === 'platform_super' || r === 'platform_admin';

interface AuditEntry {
  id: string;
  ts: string | number | null;
  actorId: string | null;
  action: string;
  entity: string;
}

interface VersionInfo {
  version: string;
}

interface HealthInfo {
  status: string;
  environment: string;
}

const INVITE_ERROR_KEYS: Record<string, string> = {
  email_taken: 'invite.errors.email_taken',
  forbidden_role: 'invite.errors.forbidden_role',
  invalid_input: 'invite.errors.invalid_input',
  role_tenant_mismatch: 'invite.errors.role_tenant_mismatch',
  tenant_not_found: 'invite.errors.tenant_not_found',
};

const TAB_IDS: ReadonlyArray<Tab> = ['users', 'audit', 'health'];

function UsersTab() {
  const { t } = useTranslation('admin');
  const { toast } = useToast();
  const { user: me } = useAuth();
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [error, setError] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('platform_ops');
  const [submitting, setSubmitting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  /** Lietotājs, kuram tiek apstiprināta MFA atiestate (modālis). */
  const [resetTarget, setResetTarget] = useState<AdminUser | null>(null);
  const [resetting, setResetting] = useState(false);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  /** Tenantu izvēle — tikai platform_super/platform_admin; noklusējums = savs (platformas) tenants. */
  const canPickTenant = me ? isPlatformAdminRole(me.role) : false;
  const [tenants, setTenants] = useState<AdminTenant[] | null>(null);
  const [tenantId, setTenantId] = useState<string>(me?.tenantId ?? '');

  const statusLabel = (code: string) => t(`statuses:${code.toLowerCase()}`, { defaultValue: code });

  const tenantType: TenantType = useMemo(() => {
    const picked = tenants?.find((x) => x.id === tenantId)?.type;
    return picked ?? (me && !me.role.startsWith('platform_') ? 'customer' : 'platform');
  }, [tenants, tenantId, me]);

  /** Lomas, ko aktors drīkst piešķirt UN kas der izvēlētā tenanta tipam (servera noteikums). */
  const allowedRoles = useMemo(
    () => (me ? assignableRoles(me.role).filter((r) => roleFitsTenantType(r, tenantType)) : []),
    [me, tenantType],
  );

  useEffect(() => {
    if (allowedRoles.length > 0 && !allowedRoles.includes(role)) setRole(allowedRoles[0] as Role);
  }, [allowedRoles, role]);

  useEffect(() => {
    if (!canPickTenant) return;
    let active = true;
    void adminListTenants().then((res) => {
      if (active && res.ok) setTenants(res.data.items ?? []);
    });
    return () => {
      active = false;
    };
  }, [canPickTenant]);

  const load = useCallback(async () => {
    setError(false);
    const query = canPickTenant && tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : '';
    const res = await api.get<{ items: AdminUser[] }>(`/api/admin/users${query}`);
    if (!res.ok) {
      setError(true);
      setUsers(null);
      return;
    }
    setUsers(res.data.items ?? []);
  }, [canPickTenant, tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function deleteInvite(u: AdminUser) {
    if (deletingId !== u.id) {
      setDeletingId(u.id);
      return;
    }
    try {
      const res = await adminDeleteInvite(u.id);
      if (!res.ok || !res.data.ok) {
        toast(t('users.deleteInviteError'), 'error');
        return;
      }
      toast(t('users.deleteInviteSuccess', { email: u.email }));
      await load();
    } catch {
      toast(t('users.deleteInviteError'), 'error');
    } finally {
      setDeletingId(null);
    }
  }

  async function resendInvite(u: AdminUser) {
    setResendingId(u.id);
    try {
      const res = await adminResendInvite(u.id);
      if (!res.ok || !res.data.ok) {
        toast(t('users.resendInviteError'), 'error');
        return;
      }
      if (res.data.emailSent) toast(t('invite.success', { email: u.email }));
      else toast(t('invite.emailNotConfigured'), 'info');
    } catch {
      toast(t('users.resendInviteError'), 'error');
    } finally {
      setResendingId(null);
    }
  }

  async function confirmResetMfa() {
    if (!resetTarget) return;
    setResetting(true);
    try {
      const res = await adminResetMfa(resetTarget.id);
      if (!res.ok || !res.data.user) {
        toast(t('users.resetMfaError'), 'error');
        return;
      }
      toast(t('users.resetMfaSuccess', { email: resetTarget.email }));
      setResetTarget(null);
      await load();
    } catch {
      toast(t('users.resetMfaError'), 'error');
    } finally {
      setResetting(false);
    }
  }

  async function onInvite(e: FormEvent) {
    e.preventDefault();
    setInviteError(null);
    if (!email.trim()) {
      setInviteError(t('invite.emailRequired'));
      return;
    }
    setSubmitting(true);
    try {
      const invitee = email.trim();
      const res = await adminInviteUser({
        email: invitee,
        role,
        ...(canPickTenant && tenantId ? { tenantId } : {}),
      });
      if (!res.ok) {
        const code = res.data.error ?? '';
        const errorKey = INVITE_ERROR_KEYS[code];
        const msg = errorKey ? t(errorKey) : t('status.error');
        setInviteError(msg);
        toast(msg, 'error');
        return;
      }
      setEmail('');
      if (res.data.emailSent) toast(t('invite.success', { email: invitee }));
      else toast(t('invite.emailNotConfigured'), 'info');
      await load();
    } catch {
      const msg = t('status.error');
      setInviteError(msg);
      toast(msg, 'error');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader title={t('invite.heading')} subtitle={t('invite.pendingNote')} />
        <CardBody>
          <form
            className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end"
            onSubmit={(e) => void onInvite(e)}
            noValidate
          >
            <Field
              label={t('invite.email')}
              htmlFor="invite-email"
              className="flex-1 sm:min-w-[16rem]"
            >
              <Input
                id="invite-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Field>
            {canPickTenant && (
              <Field label={t('invite.tenant')} htmlFor="invite-tenant">
                <Select
                  id="invite-tenant"
                  value={tenantId}
                  onChange={(e) => setTenantId(e.target.value)}
                  disabled={tenants === null}
                >
                  {TENANT_TYPES.map((type) => {
                    const group = tenants?.filter((x) => x.type === type) ?? [];
                    if (group.length === 0) return null;
                    return (
                      <optgroup key={type} label={t(`invite.tenantTypes.${type}`)}>
                        {group.map((x) => (
                          <option key={x.id} value={x.id}>
                            {x.legalName} ({x.country})
                          </option>
                        ))}
                      </optgroup>
                    );
                  })}
                </Select>
              </Field>
            )}
            <Field label={t('invite.role')} htmlFor="invite-role">
              <Select
                id="invite-role"
                value={role}
                onChange={(e) => setRole(e.target.value as Role)}
              >
                {allowedRoles.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </Select>
            </Field>
            <Button type="submit" disabled={submitting}>
              {submitting ? t('status.loading') : t('invite.submit')}
            </Button>
          </form>
          {inviteError && (
            <p className="mt-3 text-sm text-red-600 dark:text-red-400" role="alert">
              {inviteError}
            </p>
          )}
        </CardBody>
      </Card>

      {error ? (
        <DataState loading={false} error={t('status.error')} empty={false}>
          {null}
        </DataState>
      ) : users === null ? (
        <SkeletonRows rows={4} />
      ) : users.length === 0 ? (
        <EmptyState
          icon={<Users className="h-6 w-6" />}
          title={t('users.empty')}
          hint={t('users.emptyHint')}
        />
      ) : (
        <TableShell>
          <THead>
            <tr>
              <Th>{t('users.columns.email')}</Th>
              <Th>{t('users.columns.role')}</Th>
              <Th>{t('users.columns.status')}</Th>
              <Th>{t('users.columns.mfa')}</Th>
              <Th>
                <span className="sr-only">{t('users.columns.actions')}</span>
              </Th>
            </tr>
          </THead>
          <tbody>
            {users.map((u) => (
              <Tr key={u.id}>
                <Td className="font-medium text-slate-900 dark:text-white">{u.email}</Td>
                <Td>
                  <Badge tone="blue">{u.role}</Badge>
                </Td>
                <Td>
                  <StatusBadge status={u.status} label={statusLabel(u.status)} />
                </Td>
                <Td>
                  <Badge tone={u.mfaEnabled ? 'green' : 'slate'} data-testid={`mfa-badge-${u.id}`}>
                    {u.mfaEnabled ? t('users.mfaOn') : t('users.mfaOff')}
                  </Badge>
                </Td>
                <Td className="text-right">
                  {u.status === 'pending' && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={resendingId === u.id}
                      onClick={() => void resendInvite(u)}
                      data-testid={`resend-invite-${u.id}`}
                    >
                      {resendingId === u.id ? t('status.loading') : t('users.resendInvite')}
                    </Button>
                  )}
                  {u.status === 'pending' && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => void deleteInvite(u)}
                      onBlur={() => setDeletingId((cur) => (cur === u.id ? null : cur))}
                      data-testid={`delete-invite-${u.id}`}
                    >
                      {deletingId === u.id
                        ? t('users.deleteInviteConfirm')
                        : t('users.deleteInvite')}
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={u.id === me?.id || !u.mfaEnabled}
                    onClick={() => setResetTarget(u)}
                    data-testid={`reset-mfa-${u.id}`}
                  >
                    {t('users.resetMfa')}
                  </Button>
                </Td>
              </Tr>
            ))}
          </tbody>
        </TableShell>
      )}

      <Modal
        open={resetTarget !== null}
        onClose={() => {
          if (!resetting) setResetTarget(null);
        }}
        title={t('users.resetMfaTitle')}
      >
        <p className="text-sm text-slate-600 dark:text-slate-400">
          {t('users.resetMfaMessage', { email: resetTarget?.email ?? '' })}
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" disabled={resetting} onClick={() => setResetTarget(null)}>
            {t('users.resetMfaCancel')}
          </Button>
          <Button
            variant="danger"
            disabled={resetting}
            onClick={() => void confirmResetMfa()}
            data-testid="reset-mfa-confirm"
          >
            {resetting ? t('status.loading') : t('users.resetMfaConfirm')}
          </Button>
        </div>
      </Modal>
    </div>
  );
}

function AuditTab() {
  const { t, i18n } = useTranslation('admin');
  const [entries, setEntries] = useState<AuditEntry[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    void (async () => {
      const res = await api.get<{ items: AuditEntry[] }>('/api/audit');
      if (!active) return;
      if (!res.ok) {
        setError(true);
        return;
      }
      setEntries(res.data.items ?? []);
    })();
    return () => {
      active = false;
    };
  }, []);

  if (error) {
    return (
      <DataState loading={false} error={t('status.error')} empty={false}>
        {null}
      </DataState>
    );
  }
  if (entries === null) {
    return <SkeletonRows rows={5} />;
  }
  if (entries.length === 0) {
    return (
      <EmptyState
        icon={<ScrollText className="h-6 w-6" />}
        title={t('audit.empty')}
        hint={t('audit.emptyHint')}
      />
    );
  }

  return (
    <TableShell>
      <THead>
        <tr>
          <Th>{t('audit.columns.time')}</Th>
          <Th>{t('audit.columns.actor')}</Th>
          <Th>{t('audit.columns.action')}</Th>
          <Th>{t('audit.columns.entity')}</Th>
        </tr>
      </THead>
      <tbody>
        {entries.map((entry) => (
          <Tr key={entry.id}>
            <Td>{formatDateTime(entry.ts, i18n.language)}</Td>
            <Td>{entry.actorId ? `${entry.actorId.slice(0, 8)}…` : '—'}</Td>
            <Td className="font-medium text-slate-900 dark:text-white">{entry.action}</Td>
            <Td>{entry.entity}</Td>
          </Tr>
        ))}
      </tbody>
    </TableShell>
  );
}

function HealthTab() {
  const { t } = useTranslation('admin');
  const [health, setHealth] = useState<HealthInfo | null>(null);
  const [version, setVersion] = useState<VersionInfo | null>(null);
  const [error, setError] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let active = true;
    void (async () => {
      const [healthRes, versionRes] = await Promise.all([
        api.get<HealthInfo>('/health'),
        api.get<VersionInfo>('/api/version'),
      ]);
      if (!active) return;
      if (!healthRes.ok || !versionRes.ok) {
        setError(true);
        setDone(true);
        return;
      }
      setHealth(healthRes.data);
      setVersion(versionRes.data);
      setDone(true);
    })();
    return () => {
      active = false;
    };
  }, []);

  if (error) {
    return (
      <DataState loading={false} error={t('status.error')} empty={false}>
        {null}
      </DataState>
    );
  }
  if (!done) {
    return <SkeletonRows rows={3} />;
  }

  return (
    <Card>
      <CardBody>
        <dl className="space-y-2 text-sm">
          <div className="flex justify-between gap-4">
            <dt className="text-slate-500 dark:text-slate-400">{t('health.status')}</dt>
            <dd>
              <Badge tone={health?.status === 'ok' ? 'green' : 'red'}>
                {health?.status ?? '—'}
              </Badge>
            </dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-slate-500 dark:text-slate-400">{t('health.environment')}</dt>
            <dd className="font-medium">{health?.environment ?? '—'}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-slate-500 dark:text-slate-400">{t('health.version')}</dt>
            <dd className="font-medium">{version?.version ?? '—'}</dd>
          </div>
        </dl>
      </CardBody>
    </Card>
  );
}

export default function Admin() {
  const { t } = useTranslation('admin');
  const { user } = useAuth();
  const [tab, setTab] = useState<Tab>('users');

  if (!user || !user.role.startsWith('platform_')) {
    return (
      <AppLayout title={t('title')}>
        <Card>
          <CardHeader title={t('accessDenied.title')} subtitle={t('accessDenied.message')} />
          <CardBody>
            <p className="text-sm">
              <Link to="/" className="text-amber-600 hover:underline dark:text-amber-400">
                {t('accessDenied.back')}
              </Link>
            </p>
          </CardBody>
        </Card>
      </AppLayout>
    );
  }

  return (
    <AppLayout title={t('title')}>
      <div className="mb-6 flex gap-1 border-b border-slate-200 dark:border-slate-800">
        {TAB_IDS.map((id) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={
              tab === id
                ? '-mb-px border-b-2 border-amber-600 px-3 py-2 text-sm font-medium text-amber-600 dark:border-amber-400 dark:text-amber-400'
                : '-mb-px border-b-2 border-transparent px-3 py-2 text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
            }
          >
            {t(`tabs.${id}`)}
          </button>
        ))}
      </div>

      {tab === 'users' && <UsersTab />}
      {tab === 'audit' && <AuditTab />}
      {tab === 'health' && <HealthTab />}
    </AppLayout>
  );
}
